import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { db } from "@/lib/supabase";
import { recordAudit, type AuditOperation } from "@/lib/audit";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { parseISODate, startOfWeek } from "@/utils/dateUtils";
import { formatMakerDisplayName } from "@/utils/nameUtils";
import { MAX_OOO_HOURS } from "@/utils/oooConstants";
import { useToast } from "@/components/Toast";

/** Context passed by the caller so an OOO change can be labeled in the audit log. */
export interface OooAuditMeta {
  employeeName?: string | null;
  officeId?: string | null;
}

/** employeeId -> (weekKey -> out-of-office hours), where weekKey === week.start.getTime(). */
export type OooByEmployee = Map<string, Map<number, number>>;

export interface OooContextValue {
  oooByEmployee: OooByEmployee;
  /** Reload OOO rows from Supabase (for example after a calendar sync). */
  refresh: () => Promise<void>;
  /**
   * Set (or clear, when hours <= 0) an employee's out-of-office hours for one or
   * more weeks at once. A multi-week range is written per week but recorded as a
   * single audit entry spanning the affected date range.
   * @param weekKeys the timeline week keys (week.start.getTime()) to apply to.
   * @param meta optional employee name/office so the change can be recorded in the audit log.
   */
  setOoo: (employeeId: string, weekKeys: number[], hours: number, meta?: OooAuditMeta) => Promise<void>;
  /** True when a real provider is mounted (vs. the no-op fallback). */
  enabled: boolean;
  /** Whether the board is in "Add OOO" mode (cells become editable). */
  editMode: boolean;
  setEditMode: (on: boolean) => void;
}

const OooContext = createContext<OooContextValue | null>(null);

/**
 * Re-provides an existing OOO context value across a detached React root.
 * `useModal` renders outside the app provider tree, so modal callers can bridge
 * the same live OOO map instead of loading a second copy or silently disabling
 * OOO rendering.
 */
export function OooContextBridge({
  value,
  children,
}: {
  value: OooContextValue;
  children: ReactNode;
}) {
  return <OooContext.Provider value={value}>{children}</OooContext.Provider>;
}

// The data lives in the `employee_ooo` table.
const OOO_TABLE = "employee_ooo";

/** Convert a stored week_start (YYYY-MM-DD, Monday) to the timeline week key. */
function weekStartToKey(weekStart: string): number {
  return startOfWeek(parseISODate(weekStart)).getTime();
}

/** Convert a timeline week key back to a week_start date string (YYYY-MM-DD). */
function keyToWeekStart(weekKey: number): string {
  return new Date(weekKey).toISOString().slice(0, 10);
}

/** Human-readable week label for audit entries, e.g. "Jul 6, 2026". */
function formatWeekLabel(weekKey: number, withYear = true): string {
  // weekKey is a UTC-midnight Monday, so format in UTC to avoid an off-by-one day.
  return new Date(weekKey).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/**
 * Label for the affected week(s) in an audit entry: a single date, or a
 * "Jul 6 – Jul 27, 2026" range spanning the earliest to latest affected week.
 */
function formatWeekRangeLabel(weekKeys: number[]): string {
  const sorted = [...weekKeys].sort((a, b) => a - b);
  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  if (first === last) return formatWeekLabel(first);
  return `${formatWeekLabel(first, false)} – ${formatWeekLabel(last)}`;
}

/**
 * Provides per-employee out-of-office (OOO) data to the Makers and Projects
 * boards. OOO is visual-only — it never affects capacity or availability math.
 * Mount this around a board so its timelines can read and (on Makers) edit OOO.
 */
export function OooProvider({ children }: { children: ReactNode }) {
  const [oooByEmployee, setOooByEmployee] = useState<OooByEmployee>(new Map());
  const [editMode, setEditMode] = useState(false);
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const { addToast } = useToast();

  const refresh = useCallback(async () => {
    const { data, error } = await db.employeeOoo.getAll();
    if (error) {
      console.error("Failed to load out-of-office data:", error);
      return;
    }
    const next: OooByEmployee = new Map();
    for (const row of data ?? []) {
      let weeks = next.get(row.employee_id);
      if (!weeks) {
        weeks = new Map();
        next.set(row.employee_id, weeks);
      }
      weeks.set(weekStartToKey(row.week_start), row.hours);
    }
    setOooByEmployee(next);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh on remote changes. Our own writes update local state optimistically,
  // so skip the echoed refetch for self-edits.
  useRealtimeRefresh([OOO_TABLE], refresh, { skipRefetchOnSelfEdit: true });

  const applyLocal = useCallback((employeeId: string, weekKey: number, hours: number) => {
    setOooByEmployee((prev) => {
      const next = new Map(prev);
      const weeks = new Map(next.get(employeeId) ?? []);
      if (hours > 0) {
        weeks.set(weekKey, hours);
      } else {
        weeks.delete(weekKey);
      }
      next.set(employeeId, weeks);
      return next;
    });
  }, []);

  const setOoo = useCallback(
    async (employeeId: string, weekKeys: number[], hours: number, meta?: OooAuditMeta) => {
      // Clamp to the DB-allowed range (employee_ooo.hours CHECK: 0 < hours <= 80).
      // 0 or below clears the row; anything above the cap is pinned so the write
      // can't fail server-side after the optimistic update.
      const rounded = Math.min(MAX_OOO_HOURS, Math.max(0, Math.round(hours)));

      // Write each week individually (optimistic + persisted), collecting the
      // weeks that actually changed so the audit entry covers the real range.
      const affected: Array<{ weekKey: number; previous: number }> = [];
      for (const weekKey of weekKeys) {
        const previous = oooByEmployee.get(employeeId)?.get(weekKey) ?? 0;
        if (rounded === previous) continue; // nothing to do for this week
        applyLocal(employeeId, weekKey, rounded); // optimistic
        const weekStart = keyToWeekStart(weekKey);
        if (rounded > 0) {
          const { data, error } = await db.employeeOoo.upsert(employeeId, weekStart, rounded);
          if (error) {
            console.error("Failed to save OOO:", error);
            applyLocal(employeeId, weekKey, previous); // revert
            continue;
          }
          // Flag the row id so the realtime echo doesn't trigger a redundant refetch.
          if (data?.id) markSelfEdit(OOO_TABLE, data.id);
        } else {
          const { error } = await db.employeeOoo.delete(employeeId, weekStart);
          if (error) {
            console.error("Failed to clear OOO:", error);
            applyLocal(employeeId, weekKey, previous); // revert
            continue;
          }
        }
        affected.push({ weekKey, previous });
      }

      if (affected.length === 0) return;

      // One audit entry for the whole change. The affected date range is the
      // field label (shown before the hours value); the record label is just the
      // maker, so all of their OOO changes group together.
      const affectedKeys = affected.map((a) => a.weekKey);
      const prevValues = affected.map((a) => a.previous);
      const allWereEmpty = prevValues.every((v) => v === 0);
      const uniformOld = prevValues.every((v) => v === prevValues[0]) ? prevValues[0]! : null;
      const rangeLabel = formatWeekRangeLabel(affectedKeys);

      let operation: AuditOperation;
      let change: { old: unknown; new: unknown };
      if (rounded > 0) {
        operation = allWereEmpty ? "INSERT" : "UPDATE";
        change = { old: allWereEmpty ? null : uniformOld != null ? `${uniformOld}h` : null, new: `${rounded}h` };
      } else {
        operation = "DELETE";
        // Show a representative prior value (always > 0 here) so the range isn't hidden.
        change = { old: `${uniformOld ?? prevValues[0]!}h`, new: null };
      }

      recordAudit({
        table: OOO_TABLE,
        recordId: `${employeeId}:${keyToWeekStart(Math.min(...affectedKeys))}`,
        recordLabel: formatMakerDisplayName(meta?.employeeName ?? null) || "Maker",
        operation,
        changes: { [rangeLabel]: change },
        officeId: meta?.officeId ?? null,
      });

      addToast(
        rounded > 0
          ? "OOO saved locally · Calendar unchanged"
          : "OOO cleared locally · Calendar unchanged",
        "success",
        3_000,
      );
    },
    [oooByEmployee, applyLocal, markSelfEdit, addToast]
  );

  const value = useMemo<OooContextValue>(
    () => ({ oooByEmployee, refresh, setOoo, enabled: true, editMode, setEditMode }),
    [oooByEmployee, refresh, setOoo, editMode]
  );

  return <OooContext.Provider value={value}>{children}</OooContext.Provider>;
}

/** Access OOO data. Returns an empty/no-op value when no provider is mounted. */
export function useOoo(): OooContextValue {
  const ctx = useContext(OooContext);
  if (!ctx) {
    return {
      oooByEmployee: EMPTY_OOO,
      refresh: noopRefresh,
      setOoo: noopSetOoo,
      enabled: false,
      editMode: false,
      setEditMode: noopSetEditMode,
    };
  }
  return ctx;
}

const EMPTY_OOO: OooByEmployee = new Map();
async function noopSetOoo() {
  /* no provider mounted */
}
async function noopRefresh() {
  /* no provider mounted */
}
function noopSetEditMode() {
  /* no provider mounted */
}
